import React from 'react';
import { motion } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../dashboard/CustomDatePicker';
import { formatDateToLocal } from '../../hooks/useReportDateRange';

// The Today / Week / Month / (Year) / Custom pill bar plus the custom From /
// To pickers — the exact markup of the patient report pages, driven by the
// state object hooks/useReportDateRange.js returns. `children` renders in the
// same row after the tabs (the filter button, a refresh button).

const TAB_FALLBACKS = { today: 'Today', week: 'Week', month: 'Month', year: 'Year', custom: 'Custom' };

const ReportDateTabs = ({ range, tabs, layoutId, testIdPrefix, children }) => {
    const { t } = useTranslation();
    const { activeTab, setActiveTab, startDate, endDate, dateErrors, changeStartDate, changeEndDate, reset } = range;

    return (
        <div className="flex flex-col gap-4" data-testid={`${testIdPrefix}-tabs-filter-section`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2">
                <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 w-fit rounded-2xl p-1 shadow border border-gray-200 relative" data-testid={`${testIdPrefix}-tabs-container`}>
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={`relative px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD]'}`}
                            data-testid={`${testIdPrefix}-tab-${tab}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId={layoutId}
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid={`${testIdPrefix}-tab-text-${tab}`}>
                                {t(`reportsCommon.${tab}`, TAB_FALLBACKS[tab] || tab)}
                            </span>
                        </button>
                    ))}
                </div>
                {children}
            </div>

            {activeTab === 'custom' && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex flex-col sm:flex-row gap-2"
                    data-testid={`${testIdPrefix}-custom-date-section`}
                >
                    <div className="w-full sm:flex-1" data-testid={`${testIdPrefix}-from-date-field-container`}>
                        <CustomDatePicker
                            value={startDate}
                            onChange={(e) => changeStartDate(e.target.value)}
                            placeholder={t('reportsCommon.from_date', 'From Date')}
                        />
                        {dateErrors.startDate && (
                            <p className="text-red-600 text-xs mt-1" data-testid={`${testIdPrefix}-from-date-error`}>{dateErrors.startDate}</p>
                        )}
                    </div>
                    <div className="w-full sm:flex-1" data-testid={`${testIdPrefix}-to-date-field-container`}>
                        <CustomDatePicker
                            value={endDate}
                            onChange={(e) => changeEndDate(e.target.value)}
                            minDate={startDate}
                            maxDate={formatDateToLocal(new Date())}
                            placeholder={t('reportsCommon.to_date', 'To Date')}
                        />
                        {dateErrors.endDate && (
                            <p className="text-red-600 text-xs mt-1" data-testid={`${testIdPrefix}-to-date-error`}>{dateErrors.endDate}</p>
                        )}
                    </div>
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={reset}
                        className="flex items-center justify-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg px-4 py-2 hover:bg-[#4ABDBD]/10 hover:text-[#4ABDBD] transition-all cursor-pointer"
                        data-testid={`${testIdPrefix}-reset-button`}
                    >
                        <RotateCcw className="w-4 h-4" />
                        {t('reportsCommon.reset', 'Reset')}
                    </motion.button>
                </motion.div>
            )}
        </div>
    );
};

export default ReportDateTabs;
